import React, { useState, KeyboardEvent } from 'react'
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  IconButton,
  Box,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MedAgTable from '../../components/MedAgTable'
import { getPatientsList } from '../../services/CampManagementService'
import { ColDef } from 'ag-grid-community'
import { enter, ErrorMessages } from './Constants'
import styles from './patientSearchDialog.module.scss'

interface Patient {
  id?: string
  uhid?: string
  name?: string
  phoneNo?: string
  age?: number
  gender?: string
  address?: string
}

interface PatientSearchDialogProps {
  open: boolean
  onClose: () => void
  onPatientSelect?: (patient: Patient) => void
}

const PatientSearchDialog: React.FC<PatientSearchDialogProps> = ({
  open,
  onClose,
  onPatientSelect,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [patients, setPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searched, setSearched] = useState(false)

  const columnDefs: ColDef[] = [
    {
      headerName: 'UHID',
      field: 'uhid',
      width: 120,
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Name',
      field: 'name',
      width: 200,
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Phone Number',
      field: 'phoneNo',
      width: 150,
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Age',
      field: 'age',
      width: 80,
      sortable: true,
    },
    {
      headerName: 'Gender',
      field: 'gender',
      width: 100,
      sortable: true,
    },
    {
      headerName: 'Address',
      field: 'address',
      width: 250,
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Action',
      field: 'action',
      width: 150,
      cellRenderer: (params: any) => (
        <Button
          variant='outlined'
          size='small'
          onClick={() => handlePatientSelect(params.data)}
        >
          Select
        </Button>
      ),
    },
  ]

  const handleSearch = async () => {
    if (!phoneNumber?.trim()) {
      setError('Please enter a phone number')
      return
    }

    setLoading(true)
    setError('')
    setPatients([])

    try {
      const response = await getPatientsList(phoneNumber.trim())
      if (response && Array.isArray(response)) {
        setPatients(response)
        if (response?.length === 0) {
          setError(ErrorMessages.PATIENTS.NOT_FOUND)
        }
      } else {
        setPatients([])
        setError(ErrorMessages.PATIENTS.NOT_FOUND)
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to search patients')
      setPatients([])
    } finally {
      setLoading(false)
      setSearched(true)
    }
  }

  const handlePatientSelect = (patient: Patient) => {
    if (onPatientSelect) {
      onPatientSelect(patient)
    }
    handleClose()
  }

  const handleClose = () => {
    setPhoneNumber('')
    setPatients([])
    setError('')
    setSearched(false)
    onClose()
  }

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event?.key === enter) {
      handleSearch()
    }
  }

  const handleRowClick = (params: any) => {
    handlePatientSelect(params?.data)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle className={styles.dialogTitle}>
        <Typography variant='h5' className={styles.title}>
          Search Existing Patient
        </Typography>
        <IconButton onClick={handleClose} className={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <div className={styles.searchContainer}>
          <TextField
            // label='Phone Number'
            variant='outlined'
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e?.target?.value)}
            onKeyPress={handleKeyPress}
            placeholder='Enter patient phone number'
            fullWidth
            size='small'
            disabled={loading}
            className={styles.searchInput}
          />
          <div className={styles.buttonContainer}>
            <Button
              variant='contained'
              onClick={handleSearch}
              disabled={loading || !phoneNumber?.trim()}
              fullWidth
            >
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </div>
        </div>

        {error && (
          <Typography
            variant='body2'
            className={styles.errorText}
            data-testid='error-text'
          >
            {error}
          </Typography>
        )}

        {searched && (
          <Box>
            {patients?.length > 0 ? (
              <div className={styles.tableContainer}>
                <div className={styles.tableWrapper}>
                  <div className={styles.tableInnerWrapper}>
                    <MedAgTable
                      rowData={patients}
                      columnDefs={columnDefs}
                      onRowClick={handleRowClick}
                      className={styles.medAgTable}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Typography className={styles.noPatients}>
                No patients found for the given phone number
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PatientSearchDialog
